import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { useSalesOrderTargetUsers } from '@/hooks/api/1231_sales_order_target_users';
import { useUpdateQuotationTask } from '@/hooks/api/1460_update_quotation_task';
import { useQuotationTaskInvalidate } from '@/hooks/api/1480_quotation_task';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentQuotationTask } from '@/providers/QuotationTaskProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { scrollTop } from '@/utils';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useEffect } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  id: number;
  name: string;
  description?: string;
  due_date?: string;
  user_id: number;
};

export const QuotationTaskEditForm = () => {
  const navigate = useNavigate();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { setShowLoading } = useLoading();
  const { _so_ } = useCurrentSalesOrder();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { data: targetUsers } = useSalesOrderTargetUsers({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { mutate, data, isPending, error } = useUpdateQuotationTask();
  const { invalidate } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: _so_!.id,
    id: task!.id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      id: task!.id,
      name: task!.name,
      description: task!.description,
      due_date: task!.due_date,
      user_id: task!.user.id,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('見積申請タスクの編集が完了しました');
      navigate(-1);
    }
  }, [data]);

  useEffect(() => {
    if (error) scrollTop();
  }, [error]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (!targetUsers) return <></>;

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12 col-lg-9 mx-auto">
          <div className="card mb-4" id="basic-info">
            <div className="card-header d-flex align-items-center">
              <div className="col-md-8">
                <h5 className="mb-0">見積申請タスク編集</h5>
              </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body pt-0 row">
                <ApiErrorMessages error={error} />

                <div className="form-group">
                  <label>プロジェクト名</label>
                  <p className="form-text text-muted text-sm ms-1">{_so_!.name}</p>
                </div>

                <FormGroup label="タスク名" required={true}>
                  <input
                    type="text"
                    maxLength={50}
                    className={`form-control ${errors.name && 'is-invalid'}`}
                    placeholder={'タスク名'}
                    {...register('name', {
                      required: '入力して下さい。',
                    })}
                  />
                  {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                </FormGroup>

                <FormGroup label="メモ">
                  <textarea className="form-control" maxLength={2000} rows={5} {...register('description')}></textarea>
                </FormGroup>

                <div className="row">
                  <div className="col-12 col-md-6 pe-0">
                    <FormGroup label="期限日">
                      <Controller
                        control={control}
                        name="due_date"
                        render={({ field: { ref, ...field } }) => (
                          <Flatpickr
                            className={`form-control datepickr`}
                            {...field}
                            onChange={(_, dateString) => {
                              field.onChange(dateString);
                              field.onBlur();
                            }}
                            onBlur={() => {}}
                            options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                          />
                        )}
                      />
                    </FormGroup>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <FormGroup label="タスク担当者" required={true}>
                    <select className="form-control" required={true} {...register('user_id')}>
                      {targetUsers.map((tu, i) => (
                        <option key={`target-user-${tu.id}`} value={tu.id}>
                          {tu.user_account.name}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                  戻る
                </Link>
                <button className="btn bg-gradient-dark btn-icon">
                  <i className="fas fa-check-circle"></i>変更を確定する
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
